import { HAS_CLOSED_SIGNUP_MODAL } from "@faire/deprecated-retailer-visitor-shared/components/AppSignUp/state/consts";
import { useIsLoggedInRetailerOrBrandPreview } from "@faire/deprecated-retailer-visitor-shared/lib/isLoggedInRetailer";
import { isComingFromFacebookOrInstagram } from "@faire/deprecated-retailer-visitor-shared/lib/navigation-origin/isComingFromFacebookOrInstagram";
import {
  popSignUp,
  SignUpElement,
  SignUpType,
} from "@faire/deprecated-retailer-visitor-shared/lib/popSignUp";
import { getIsProduction } from "@faire/deprecated-retailer-visitor-shared/serialized-data/getEnvName";
import { getIsRobot } from "@faire/deprecated-retailer-visitor-shared/serialized-data/getIsRobot";
import { useAppModal } from "@faire/deprecated-retailer-visitor-shared/stores/ui/AppModalStore";
import { route as brandRoute } from "@faire/web-api--source/routes/www/brand/brandToken";
import { route as c1Route } from "@faire/web-api--source/routes/www/category/c1CategoryUrlId";
import { route as c1BrandValueRoute } from "@faire/web-api--source/routes/www/category/c1CategoryUrlId/brand-value/brandValue";
import { route as c2Route } from "@faire/web-api--source/routes/www/category/c1CategoryUrlId/subcategory/c2CategoryUrlId";
import { route as c2BrandValueRoute } from "@faire/web-api--source/routes/www/category/c1CategoryUrlId/subcategory/c2CategoryUrlId/brand-value/brandValue";
import { route as c3Route } from "@faire/web-api--source/routes/www/category/c1CategoryUrlId/subcategory/c2CategoryUrlId/c3CategoryUrlId";
import { route as c3BrandValueRoute } from "@faire/web-api--source/routes/www/category/c1CategoryUrlId/subcategory/c2CategoryUrlId/c3CategoryUrlId/brand-value/brandValue";
import { route as discoverRoute } from "@faire/web-api--source/routes/www/discover/searchTerm";
import { route as collectionRoute } from "@faire/web-api--source/routes/www/product-based-collection/product_based_collection_token";
import { route as productRoute } from "@faire/web-api--source/routes/www/product/productToken";
import { route as homeRoute } from "@faire/web-api--source/routes/www/root";
import { route as searchRoute } from "@faire/web-api--source/routes/www/search";
import { route as suppliersC1Route } from "@faire/web-api--source/routes/www/suppliers/c1SuppliersUrlId";
import { route as suppliersC1BrandValueRoute } from "@faire/web-api--source/routes/www/suppliers/c1SuppliersUrlId/brand-value/brandValue";
import { route as suppliersC2Route } from "@faire/web-api--source/routes/www/suppliers/c1SuppliersUrlId/subcategory/c2SuppliersUrlId";
import { route as suppliersC3Route } from "@faire/web-api--source/routes/www/suppliers/c1SuppliersUrlId/subcategory/c2SuppliersUrlId/c3SuppliersUrlId";
import { RouteMatcher } from "@faire/web/common/routes/RouteMatcher";
import { Storage } from "@faire/web/common/Storage";
import { useDetectScroll } from "@faire/web/ui/hooks/useDetectScroll";
import { useEffect, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";

import { useFaireDirectInfo } from "../hooks/FaireDirect/useFaireDirectInfo/useFaireDirectInfo";
import { LocationForOrigin } from "../lib/navigation-origin/LocationForOrigin";
import { useIsLoggedOutLightGateTreatmentWithEligibility } from "../settings/useSettingLoggedOutLightGate";

const enum SignUpShownStatus {
  NOT_SHOWN,
  SHOWN,
}

export const AutoSignUpInitializer: React.FC = () => {
  const isLoggedInRetailerOrBrandPreview =
    useIsLoggedInRetailerOrBrandPreview();
  const isProduction = getIsProduction();
  const isRobot = getIsRobot();

  const [popSignUpStatus, setPopSignUpStatus] = useState<SignUpShownStatus>(
    SignUpShownStatus.NOT_SHOWN
  );

  if (
    popSignUpStatus === SignUpShownStatus.SHOWN ||
    isLoggedInRetailerOrBrandPreview ||
    !isProduction ||
    isRobot ||
    !!Storage.session.getItem(HAS_CLOSED_SIGNUP_MODAL)
  ) {
    return null;
  }

  return <AutoSignUpInitializerBase setPopSignUpStatus={setPopSignUpStatus} />;
};

const AutoSignUpInitializerBase: React.FC<{
  setPopSignUpStatus: (status: SignUpShownStatus) => void;
}> = ({ setPopSignUpStatus }) => {
  const history = useHistory();
  const location = useLocation();
  const { isModalShowing } = useAppModal();
  const { faireDirectBrandToken } = useFaireDirectInfo();
  const isFaireDirectActiveInLoggedOut = !!faireDirectBrandToken;
  const isLightGatingTreatment =
    useIsLoggedOutLightGateTreatmentWithEligibility();

  // Check scroll only every 10px to reduce the number of checks
  const { scrollPosition } = useDetectScroll("y", 10);
  const [previousScrollPosition, setPreviousScrollPosition] =
    useState(scrollPosition);

  useEffect(() => {
    setPreviousScrollPosition(scrollPosition);
  }, [scrollPosition]);

  useEffect(() => {
    if (
      !isLightGatingTreatment &&
      previousScrollPosition !== scrollPosition &&
      shouldPopSignUp(location, isModalShowing, isFaireDirectActiveInLoggedOut)
    ) {
      setPopSignUpStatus(SignUpShownStatus.SHOWN);
      popSignUp(history, SignUpType.AUTO, () => {}, {
        element: SignUpElement.AUTO_POPUP,
        sourceId: "auto_signup",
      });
    }
  }, [
    history,
    isFaireDirectActiveInLoggedOut,
    isModalShowing,
    location,
    previousScrollPosition,
    scrollPosition,
    setPopSignUpStatus,
    isLightGatingTreatment,
  ]);

  return null;
};

export const shouldPopSignUp = (
  location: LocationForOrigin,
  isModalShowing: boolean,
  isFaireDirectActiveInLoggedOut: boolean
) => {
  const matcher = new RouteMatcher(whitelist);

  return (
    !isComingFromFacebookOrInstagram() &&
    !isFaireDirectActiveInLoggedOut &&
    !isModalShowing &&
    matcher.match(location?.pathname ?? "") &&
    !getIsRobot()
  );
};

export const whitelist = [
  homeRoute,
  brandRoute,
  productRoute,
  collectionRoute,
  searchRoute,
  discoverRoute,
  c1Route,
  c1BrandValueRoute,
  c2Route,
  c2BrandValueRoute,
  c3Route,
  c3BrandValueRoute,
  suppliersC3Route,
  suppliersC1Route,
  suppliersC1BrandValueRoute,
  suppliersC2Route,
];
